import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogButton,
  DialogActions,
  DialogContent,
  TextField,
} from '@engyalo/design-system';
import { ReactElement, useState } from 'react';

type DialogProps = React.ComponentProps<typeof Dialog>;

export interface DialogConfirmationProps {
  open: boolean;
  title?: string;
  description?: ReactElement | string;
  disabled?: boolean;
  loading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmationKeyword?: string;
  variant?: DialogProps['variant'];
  onCancel?: () => void;
  onConfirm?: () => void;
}

const DEFAULT_VARIANT = 'info';

const DialogConfirmation = ({
  variant = DEFAULT_VARIANT,
  open,
  title,
  description,
  disabled,
  confirmationKeyword,
  confirmLabel,
  cancelLabel,
  onCancel,
  onConfirm,
}: DialogConfirmationProps) => {
  const { t } = useTranslation();

  const [keyword, setKeyword] = useState('');
  const descriptionContent = typeof description === 'string' ? t(description) : description;

  const showCancelButton = !!onCancel;
  const showConfirmButton = !!onConfirm;
  const showActions = showCancelButton || showConfirmButton;
  const showDescription = !!description;
  const shouldShowConfirmationKeywordInput = !!confirmationKeyword;
  const requiresKeywordConfirmation = !!confirmationKeyword;
  const isKeywordMatching = keyword === confirmationKeyword;
  const shouldDisableConfirmButton = disabled || (requiresKeywordConfirmation && !isKeywordMatching);

  const handleCancel = () => {
    setKeyword('');
    onCancel?.();
  };

  const handleConfirm = () => {
    if (requiresKeywordConfirmation && !isKeywordMatching) {
      return;
    }
    onConfirm?.();
    setKeyword('');
  };

  const onCloseAction = disabled ? undefined : handleCancel || handleConfirm;

  return (
    <Dialog onClose={onCloseAction} variant={variant} open={open}>
      <DialogTitle>
        {title}
        {showDescription && <DialogContentText>{descriptionContent}</DialogContentText>}
      </DialogTitle>
      {shouldShowConfirmationKeywordInput && (
        <DialogContent>
          <TextField
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            label={`${t('confirmationDialog.confirmationKeyword')} ${confirmationKeyword}`}
            autoComplete="off"
          />
        </DialogContent>
      )}
      {showActions && (
        <DialogActions>
          {showCancelButton && (
            <DialogButton onClick={handleCancel} disabled={disabled}>
              {cancelLabel || t('confirmationDialog.cancel')}
            </DialogButton>
          )}
          {showConfirmButton && (
            <DialogButton onClick={handleConfirm} primary disabled={shouldDisableConfirmButton}>
              {confirmLabel || t('confirmationDialog.confirm')}
            </DialogButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DialogConfirmation;
